import useEleves from "hooks/useEleves";
import mixpanel from "mixpanel-browser";
import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "context/AuthContext";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addBearerToken, clearUserInfo } from "redux_dir/features/userSlice";
import { clearParentForm } from "redux_dir/features/parentSlice";
import { baseApi } from "redux_dir/features/apiMananger";
import { Loader } from "tabler-icons-react";

/**
 * Ancienne page "Qui va apprendre ?".
 *
 * Cette étape faisait perdre plus de la moitié des parents avant même
 * le premier diagnostic : elle est maintenant un simple aiguillage.
 * On ouvre directement la page matière de l'enfant (le changement d'enfant
 * se fait désormais depuis le sélecteur en haut de cette page).
 *
 * La route est conservée pour ne casser aucun lien existant
 * (retour de diagnostic, aide aux devoirs, liens partagés, etc.).
 */
function SelectEnfantPage() {
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const redirected = useRef(false);

  const { logout } = useContext(AuthContext);

  const {
    parent,
    eleves,
    defaultEleve,
    isLoading,
    isError,
    isUnauthorized,
    error,
    refetch,
  } = useEleves();

  // Session expirée (401) : inutile de réessayer, on déconnecte proprement
  // pour que le parent se reconnecte au lieu de rester bloqué sur l'erreur.
  useEffect(() => {
    if (!isUnauthorized) return;
    mixpanel.track("Session Expirée Auto Déconnexion");
    logout?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnauthorized]);

  // TRK-1 : mesurer la durée de chargement du profil enfant
  const [loadStart] = useState(() => Date.now());

  // NET-1 : retry automatique (2 tentatives avec délai croissant 3s, 6s)
  const retryCount = useRef(0);
  const [isRetrying, setIsRetrying] = useState(false);

  // Le token peut ne pas être encore dans le store si on arrive directement ici
  useEffect(() => {
    try {
      const stored = localStorage.getItem("auth_token");
      if (stored) {
        dispatch(addBearerToken(stored));
        if (parent?.id) refetch?.();
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, parent?.id]);

  // NET-1 : retry automatique sur erreur (max 2, délai croissant)
  useEffect(() => {
    if (
      isError &&
      !isUnauthorized &&
      retryCount.current < 2 &&
      !redirected.current
    ) {
      retryCount.current++;
      setIsRetrying(true);
      const delay = retryCount.current * 3000; // 3s puis 6s
      const timer = setTimeout(() => {
        refetch?.();
      }, delay);
      return () => clearTimeout(timer);
    }
    if (!isError) {
      setIsRetrying(false);
      retryCount.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isUnauthorized]);

  // STATE-1 : revalider l'état au retour d'arrière-plan
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !redirected.current) {
        // Vérifier que le token est toujours présent
        try {
          const token = localStorage.getItem("auth_token");
          if (!token) {
            redirected.current = true;
            dispatch(clearUserInfo());
            dispatch(clearParentForm());
            dispatch(baseApi.util.resetApiState());
            navigation("/parentProspect", { replace: true });
            return;
          }
        } catch {}
        // Recharger les données au retour d'arrière-plan
        refetch?.();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  useEffect(() => {
    if (redirected.current) return;

    // Si le chargement est encore en cours, on attend avant de juger
    if (isLoading) return;

    // Si l'ID parent a disparu après chargement (état corrompu), on force la reconnexion
    if (!parent?.id) {
      redirected.current = true;
      try { localStorage.removeItem("auth_token"); } catch {}
      dispatch(clearUserInfo());
      dispatch(clearParentForm());
      dispatch(baseApi.util.resetApiState());
      navigation("/parentProspect", { replace: true });
      return;
    }

    if (isError) return;

    // Aucun enfant : on envoie directement vers la création d'un profil
    if (!eleves.length) {
      redirected.current = true;
      mixpanel.track("Redirection Auto Creation Enfant");
      navigation("/parentProspect/form", {
        replace: true,
        state: { nombre: 1 },
      });
      return;
    }

    if (!defaultEleve?.id) return;

    redirected.current = true;
    mixpanel.track("Redirection Auto Matiere", {
      "Enfant ID": defaultEleve?.id,
      Classe: defaultEleve?.classe?.name,
      "Nombre Enfants": eleves.length,
    });
    navigation(`/${defaultEleve?.id}/matiere`, { replace: true });
  }, [
    parent?.id,
    isLoading,
    isError,
    eleves.length,
    defaultEleve?.id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    navigation,
  ]);

  if (isError && isRetrying) {
    // NET-1 : retry en cours, on affiche un loader au lieu de l'erreur
    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center gap-4 bg-gradient-to-b from-primary to-secondary text-white">
        <img src="/icons/icon1.png" alt="Genimi" style={{ width: 64 }} />
        <Loader size={28} className="animate-spin" />
        <p className="text-sm font-medium opacity-90">Nouvelle tentative…</p>
      </div>
    );
  }

  if (isError && !isUnauthorized) {
    // 401/403 = session expirée ou token absent : réessayer ne changera rien,
    // il faut que le parent se reconnecte. Tout le reste = erreur réseau/serveur.
    const sessionExpiree =
      error?.status === 401 || error?.status === 403;

    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center gap-4 px-8 text-center bg-white">
        <p className="text-primary font-bold text-lg">
          {sessionExpiree
            ? "Votre session a expiré"
            : "Impossible de charger le profil de votre enfant"}
        </p>
        <p className="text-primary/60 text-sm">
          {sessionExpiree
            ? "Reconnectez-vous avec votre numéro de téléphone pour continuer."
            : "Vérifiez votre connexion internet puis réessayez."}
        </p>
        <button
          onClick={() => {
            if (sessionExpiree) {
              // Vider la session avant de naviguer pour éviter une boucle de redirection.
              try { localStorage.removeItem("auth_token"); } catch {}
              dispatch(clearUserInfo());
              dispatch(clearParentForm());
              dispatch(baseApi.util.resetApiState());
              navigation("/parentProspect", { replace: true });
              return;
            }
            retryCount.current = 0;
            setIsRetrying(false);
            refetch?.();
          }}
          className="mt-2 bg-primary text-white font-bold px-6 py-3 rounded-full"
        >
          {sessionExpiree ? "Se reconnecter" : "Réessayer"}
        </button>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-center gap-4 bg-gradient-to-b from-primary to-secondary text-white">
      <img src="/icons/icon1.png" alt="Genimi" style={{ width: 64 }} />
      <Loader size={28} className="animate-spin" />
      <p className="text-sm font-medium opacity-90">Préparation du suivi…</p>
    </div>
  );
}

export default SelectEnfantPage;
